import React, { useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { useStripe } from "../hooks/useStripe";
import { useAccountType } from "../hooks/useAccountType";
import Toast from "./Toast";
import "./SubscriptionPanel.css";

/**
 * SubscriptionPanel Component
 * Displays and manages user subscriptions
 * Features:
 * - View current subscription status
 * - Show renewal date & amount
 * - Upgrade plan
 * - Cancel subscription
 * - Billing history
 */
export default function SubscriptionPanel() {
  const { user } = useAuth();
  const { 
    plan, 
    subscriptionDetails, 
    loading: accountLoading,
    isAdmin,
    isBroker,
    hasActiveSubscription
  } = useAccountType();
  const {
    cancelSubscription,
    loading: stripeLoading,
    clearError,
  } = useStripe();

  const [toast, setToast] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [immediateCancel, setImmediateCancel] = useState(false);

  const productIdByPlan = {
    agent: "agent",
    leader: "leader",
    broker: "broker",
    senior_broker: "senior_broker",
  };

  // Map account type to internal plan key
  const mapAccountTypeToKey = (type) => {
    switch (String(type || "").toLowerCase()) {
      case "agent":
        return "agent";
      case "leader":
        return "leader";
      case "broker":
        return "broker";
      case "senior broker":
        return "senior_broker";
      case "admin":
        return null; // Managed by admin
      default:
        return "new_agent";
    }
  };

  const currentPlanKey = mapAccountTypeToKey(plan);

  // Plan details
  const plans = {
    new_agent: {
      name: "New Agent",
      price: 0,
      period: "Free",
      features: ["Basic CRM", "1 Prospect", "Limited Reports"],
      buttonText:
        currentPlanKey === "new_agent"
          ? "Current Plan"
          : hasActiveSubscription
          ? "Cancel to switch to Free"
          : "Switch to Free",
      buttonDisabled:
        accountLoading || currentPlanKey === "new_agent" || hasActiveSubscription || isAdmin || isBroker,
    },
    agent: {
      name: "Agent",
      price: 14.99,
      period: "month",
      features: ["Full CRM", "Unlimited Prospects", "Advanced Reports", "Team Features"],
      buttonText: currentPlanKey === "agent" ? "Current Plan" : "Upgrade to Agent",
      buttonDisabled: accountLoading || currentPlanKey === "agent" || isAdmin || isBroker,
    },
    leader: {
      name: "Leader",
      price: 24.99,
      period: "month",
      features: ["Everything in Agent", "Team Management", "Custom Reports"],
      buttonText: currentPlanKey === "leader" ? "Current Plan" : "Upgrade to Leader",
      buttonDisabled: accountLoading || currentPlanKey === "leader" || isAdmin || isBroker,
    },
    broker: {
      name: "Broker",
      price: 69.99,
      period: "month",
      features: ["Everything in Leader", "Broker Tools", "Multi-team Management", "Advanced Analytics"],
      buttonText: currentPlanKey === "broker" ? "Current Plan" : "Upgrade to Broker",
      buttonDisabled: accountLoading || currentPlanKey === "broker" || isAdmin,
    },
    senior_broker: {
      name: "Senior Broker",
      price: 99.99,
      period: "month",
      features: ["Everything in Broker", "White Label Options", "API Access", "Priority Support"],
      buttonText: currentPlanKey === "senior_broker" ? "Current Plan" : "Upgrade to Senior Broker",
      buttonDisabled: accountLoading || currentPlanKey === "senior_broker" || isAdmin,
    },
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = typeof timestamp === "number" ? new Date(timestamp * 1000) : new Date(timestamp);
    return date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  };

  // Get status color and text
  const getStatusDisplay = () => {
    if (!subscriptionDetails) return { color: "gray", text: "No subscription" };

    const status = subscriptionDetails.status;
    switch (status) {
      case "active":
      case "trialing":
        return { color: "green", text: "Active" };
      case "past_due":
        return { color: "orange", text: "Past Due" };
      case "canceled":
        return { color: "red", text: "Canceled" };
      default:
        return { color: "gray", text: status };
    }
  };

  const statusDisplay = getStatusDisplay();

  // Handle plan upgrade
  const handleUpgradeClick = async (planKey) => {
    // Admin/Broker managed accounts cannot self-change plans
    if (isAdmin || isBroker) {
      setToast({ message: "Admin/Broker accounts are managed by administrators.", type: "error" });
      return;
    }

    // Prevent selecting the current plan
    if (planKey === currentPlanKey) {
      setToast({ message: "You are already on this plan.", type: "error" });
      return;
    }

    // Prevent downgrading to free via upgrade flow
    if (planKey === "new_agent" && hasActiveSubscription) {
      setToast({ message: "Use 'Cancel Subscription' to downgrade to the free plan.", type: "error" });
      return;
    }

    const stripeProductId = productIdByPlan[planKey];
    if (!stripeProductId) {
      setToast({ message: "Invalid plan selected for checkout.", type: "error" });
      return;
    }

    try {
      setProcessingPayment(true);
      clearError();

      if (!user?.uid) {
        throw new Error("User is not authenticated.");
      }

      const response = await fetch(
        "https://wealth-builder-backend-939575270287.us-east1.run.app/api/v1/create-subscription",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            stripe_product_id: stripeProductId,
            user_id: user.uid,
          }),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message = data?.message || "Failed to create Stripe checkout session.";
        throw new Error(message);
      }

      const checkoutUrl =
        data?.checkout_url ||
        data?.checkoutUrl ||
        data?.data?.checkout_url ||
        data?.data?.checkoutUrl;

      if (!checkoutUrl) {
        throw new Error("Checkout URL was not returned by the API.");
      }

      window.location.assign(checkoutUrl);
    } catch (error) {
      setToast({ message: error.message || "Upgrade failed", type: "error" });
    } finally {
      setProcessingPayment(false);
    }
  };

  // Handle subscription cancellation
  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async () => {
    try {
      setProcessingPayment(true);
      clearError();

      await cancelSubscription(immediateCancel);
      setToast({
        message: immediateCancel
          ? "Subscription canceled immediately"
          : "Subscription will cancel at period end",
        type: "success",
      });

      setShowCancelModal(false);
      setImmediateCancel(false);
      setProcessingPayment(false);
    } catch (error) {
      setToast({ message: error.message || "Cancellation failed", type: "error" });
      setProcessingPayment(false);
    }
  };

  if (accountLoading) {
    return <div className="subscription-panel loading">Loading subscription info...</div>;
  }

  return (
    <div className="subscription-panel">
      {/* Current Subscription Status */}
      <div className="subscription-status">
        <h3>Current Subscription</h3>
        <div className="status-info">
          <div className="status-item">
            <span className="label">Plan:</span>
            <span className="value">{plan || "New Agent"}</span>
          </div>

          <div className="status-item">
            <span className="label">Status:</span>
            <span className={`value status-${statusDisplay.color}`}>{statusDisplay.text}</span>
          </div>

          {subscriptionDetails?.renewalDate && (
            <div className="status-item">
              <span className="label">Next Renewal:</span>
              <span className="value">{formatDate(subscriptionDetails.renewalDate)}</span>
            </div>
          )}

          {subscriptionDetails?.amount && (
            <div className="status-item">
              <span className="label">Monthly Cost:</span>
              <span className="value">${(subscriptionDetails.amount / 100).toFixed(2)}</span>
            </div>
          )}
        </div>

        {subscriptionDetails?.hasSubscription && (
          <button
            className="btn-cancel"
            onClick={handleCancelClick}
            disabled={stripeLoading || processingPayment}
          >
            {stripeLoading || processingPayment ? "Processing..." : "Cancel Subscription"}
          </button>
        )}
      </div>

      {/* Upgrade Plans */}
      <div className="upgrade-section">
        <h3>Available Plans</h3>
        <div className="plans-grid">
          {Object.entries(plans).map(([planKey, plan]) => (
            <div key={planKey} className={`plan-card ${plan.buttonDisabled ? "current" : ""}`}>
              <h4>{plan.name}</h4>
              <div className="price">
                {plan.price === 0 ? (
                  <span className="free">Free</span>
                ) : (
                  <>
                    <span className="amount">${plan.price}</span>
                    <span className="period">/{plan.period}</span>
                  </>
                )}
              </div>

              <ul className="features">
                {plan.features.map((feature, idx) => (
                  <li key={idx}>✓ {feature}</li>
                ))}
              </ul>

              <button
                className={`btn-upgrade ${plan.buttonDisabled ? "disabled" : ""}`}
                onClick={() => handleUpgradeClick(planKey)}
                disabled={plan.buttonDisabled || stripeLoading || processingPayment}
              >
                {stripeLoading || processingPayment ? "Processing..." : plan.buttonText}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/*
      Upgrade modal with in-app card entry is intentionally disabled.
      Checkout is now handled by Stripe hosted checkout via backend API.

      {showUpgradeModal && (
        <div className="modal-overlay" onClick={() => setShowUpgradeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Upgrade to {plans[selectedPlan]?.name}</h3>
            <p>Monthly cost: ${plans[selectedPlan]?.price || 0}</p>

            {selectedPlan !== "new_agent" && (
              <div className="payment-form">
                <label>Card Details</label>
                <CardElement />
              </div>
            )}

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowUpgradeModal(false)}
                disabled={processingPayment}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleUpgradeConfirm}
                disabled={processingPayment}
              >
                {processingPayment ? "Processing..." : "Confirm Upgrade"}
              </button>
            </div>
          </div>
        </div>
      )}
      */}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Cancel Subscription</h3>
            <p>Are you sure you want to cancel your subscription?</p>

            <div className="cancel-options">
              <label>
                <input
                  type="radio"
                  checked={!immediateCancel}
                  onChange={() => setImmediateCancel(false)}
                />
                <span>Cancel at period end (retain access until {formatDate(subscriptionDetails?.renewalDate)})</span>
              </label>
              <label>
                <input
                  type="radio"
                  checked={immediateCancel}
                  onChange={() => setImmediateCancel(true)}
                />
                <span>Cancel immediately (lose access now)</span>
              </label>
            </div>

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowCancelModal(false)}
                disabled={processingPayment}
              >
                Keep Subscription
              </button>
              <button
                className="btn-danger"
                onClick={handleCancelConfirm}
                disabled={processingPayment}
              >
                {processingPayment ? "Processing..." : "Cancel Subscription"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
